import React from 'react';
import { FIAdvisorChat } from './fi-advisor-chat-section.jsx';

// =============================================
// FI ADVISOR SECTION COMPONENT
// =============================================

export function FIAdvisorSection({ generateFIReport }) {
    const report = generateFIReport();
    const sym = report.baseCurrency === 'LKR' ? '₨' : report.baseCurrency === 'USD' ? '$' : 'S$';
    const fmt = (n) => sym + Math.abs(Math.round(n || 0)).toLocaleString();
    const loans = report.personalLoans?.loans || [];
    const totalLoanMonthly = report.personalLoans?.totalMonthlyInstallmentBase || 0;
    const totalLoanRemaining = report.personalLoans?.totalRemainingBase || 0;
    const totalCash = report.bankAccounts?.totalBase || 0;
    const totalGold = report.summary?.totalGoldValue || 0;
    const totalCC = report.summary?.totalCreditCardOutstanding || 0;
    const netWorth = report.summary?.netWorthEstimate || 0;
    const lkrRate = report.exchangeRates?.lkrPerSgd || 244;

    const allBudgetItems = [
        ...(report.budgets?.sgCycle1 || []),
        ...(report.budgets?.sgCycle2 || []),
    ];
    const totalBudgeted = allBudgetItems.filter(i => i.enabled).reduce((s, i) => s + (i.amountBase || 0), 0);
    const totalSpent = allBudgetItems.filter(i => i.enabled).reduce((s, i) => s + (i.actualSpentBase || 0), 0);
    const surplus = totalBudgeted - totalSpent;

    const buildClaudeContext = () => {
        const lns = loans.map((l, i) =>
            `LOAN-${String(i + 1).padStart(2, '0')}: ${l.type}, ${sym}${Math.round(l.principalRemaining)} remaining, ${l.monthsLeft}m left, ${sym}${Math.round(l.monthlyInstallment)}/mo, ends ${l.settlementDate || 'n/a'}`
        ).join('\n');
        const accs = (report.bankAccounts?.aggregatedByRegionCurrency || []).map(a =>
            `${a.country} ${a.currency}: ${sym}${Math.round(a.totalBase)}`
        ).join(', ');
        const budgetLines = allBudgetItems.filter(i => i.enabled).map(i =>
            `  ${i.category}: budgeted ${sym}${Math.round(i.amountBase)}, spent ${sym}${Math.round(i.actualSpentBase)}`
        ).join('\n');
        return `=== iPlanner FI Report (${new Date().toDateString()}) ===
BASE CURRENCY: ${report.baseCurrency}
EXCHANGE RATES: LKR/${report.baseCurrency} = ${lkrRate}, USD/${report.baseCurrency} = ${report.exchangeRates?.sgdPerUsd}

FINANCIAL SUMMARY:
- Total Cash & Bank: ${fmt(totalCash)}
- Total Gold (market value): ${fmt(totalGold)}
- Total Personal Loan Remaining: ${fmt(totalLoanRemaining)}
- Total Monthly Loan Payments: ${fmt(totalLoanMonthly)}
- Credit Card Outstanding: ${fmt(totalCC)}
- Financial Net Worth: ${fmt(netWorth)}

LOANS:
${lns}

BANK ACCOUNTS:
${accs}

BUDGET (current cycle):
${budgetLines}
Total Budgeted: ${fmt(totalBudgeted)} | Total Spent: ${fmt(totalSpent)} | Surplus: ${fmt(surplus)}

GOLD INVENTORY:
${(report.goldInventory?.byTypeAndQuality || []).map(g => `  ${g.type} (${g.quality}): ${g.count} items, ${g.totalWeightG}g, value ${fmt(g.totalValueBase)}`).join('\n')}`;
    };

    const handleLoadToClaude = () => {
        const context = buildClaudeContext();
        const prompt = `You are my personal financial advisor. Here is my complete financial data from iPlanner:\n\n${context}\n\nPlease analyze my financial situation and give me specific, actionable advice. Start with a brief summary of my overall position, then highlight the 3 most important things I should focus on right now.`;
        const url = `https://claude.ai/new?q=${encodeURIComponent(prompt)}`;
        window.open(url, '_blank');
    };

    const handleCopyContext = () => {
        const context = buildClaudeContext();
        navigator.clipboard.writeText(context).then(() => {
            alert('Financial context copied! Paste it into any AI chat.');
        });
    };

    return (
        <div className="section-container" style={{ paddingBottom: '100px' }}>
            {/* Header */}
            <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <div>
                        <h2 style={{ fontSize: '20px', fontWeight: '700', color: 'var(--text-primary)', margin: 0 }}>🤖 FI Advisor</h2>
                        <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '3px' }}>Live data · {new Date().toLocaleDateString()}</div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <button onClick={handleCopyContext} style={{ fontSize: '12px', padding: '7px 12px', background: 'rgba(167,139,250,0.12)', border: '1px solid rgba(167,139,250,0.3)', borderRadius: '10px', color: '#a78bfa', cursor: 'pointer', fontFamily: 'inherit' }}>
                            📋 Copy
                        </button>
                        <button onClick={handleLoadToClaude} style={{ fontSize: '12px', padding: '7px 12px', background: 'linear-gradient(135deg, #7c3aed, #a855f7)', border: 'none', borderRadius: '10px', color: '#fff', cursor: 'pointer', fontFamily: 'inherit', fontWeight: '600' }}>
                            ✨ Load to Claude
                        </button>
                    </div>
                </div>
                {/* Load to Claude Banner */}
                <div style={{ background: 'linear-gradient(135deg, rgba(124,58,237,0.15), rgba(168,85,247,0.08))', border: '1px solid rgba(167,139,250,0.25)', borderRadius: '12px', padding: '12px 14px', fontSize: '12px', color: '#c4b5fd', lineHeight: '1.5' }}>
                    <strong style={{ color: '#a78bfa' }}>Load to Claude</strong> — Sends your live financial data as context so Claude can give personalized advice. Or use <strong>Copy</strong> to paste into any AI.
                </div>
            </div>

            {/* Key Metrics */}
            <div style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-tertiary)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '8px' }}>Financial Snapshot</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '16px' }}>
                {[
                    { label: 'Cash & Bank', value: fmt(totalCash), color: '#10b981' },
                    { label: 'Gold Value', value: fmt(totalGold), color: '#f59e0b' },
                    { label: 'Total Loans', value: fmt(totalLoanRemaining), color: '#ef4444' },
                    { label: 'Monthly Pmts', value: fmt(totalLoanMonthly), color: '#f59e0b' },
                    { label: 'Budget Surplus', value: fmt(surplus), color: surplus >= 0 ? '#10b981' : '#ef4444' },
                    { label: 'Net Worth', value: fmt(netWorth), color: netWorth >= 0 ? '#10b981' : '#ef4444' },
                ].map((m, i) => (
                    <div key={i} style={{ background: 'var(--card-bg, rgba(255,255,255,0.04))', border: '1px solid var(--border)', borderRadius: '10px', padding: '10px 10px' }}>
                        <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginBottom: '4px' }}>{m.label}</div>
                        <div style={{ fontSize: '14px', fontWeight: '700', color: m.color }}>{m.value}</div>
                    </div>
                ))}
            </div>

            {/* Loans List */}
            <div style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-tertiary)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '8px' }}>Active Loans</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                {loans.length === 0 ? (
                    <div style={{ padding: '16px', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: '13px' }}>No active loans</div>
                ) : loans.map((loan, i) => {
                    const pct = loan.principalRemainingBase && (loan.principalRemainingBase / (loan.principalRemainingBase + (loan.principalPaid || 0))) * 100;
                    return (
                        <div key={i} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '12px', padding: '12px 14px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                                <div>
                                    <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)' }}>LOAN-{String(i + 1).padStart(2, '0')} · {loan.type}</div>
                                    <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '2px' }}>{loan.monthsLeft}m left · {fmt(loan.monthlyInstallment)}/mo · ends {loan.settlementDate || 'n/a'}</div>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                    <div style={{ fontSize: '14px', fontWeight: '700', color: '#ef4444' }}>{fmt(loan.principalRemainingBase)}</div>
                                    <div style={{ fontSize: '10px', color: 'var(--text-tertiary)' }}>remaining</div>
                                </div>
                            </div>
                            <div style={{ height: '4px', background: 'rgba(255,255,255,0.08)', borderRadius: '2px', overflow: 'hidden' }}>
                                <div style={{ height: '100%', width: `${Math.min(100, 100 - (pct || 0))}%`, background: '#6366f1', borderRadius: '2px', transition: 'width 0.5s' }} />
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Budget Overview */}
            <div style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-tertiary)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '8px' }}>Budget This Cycle</div>
            <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '12px', padding: '12px 14px', marginBottom: '16px' }}>
                {allBudgetItems.filter(i => i.enabled && (i.amountBase || 0) > 0).map((item, i) => {
                    const pct = item.amountBase > 0 ? Math.round((item.actualSpentBase / item.amountBase) * 100) : 0;
                    const over = pct > 100;
                    return (
                        <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', alignItems: 'center', padding: '7px 0', borderBottom: i < allBudgetItems.filter(i => i.enabled).length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>
                            <span style={{ fontSize: '12px', color: 'var(--text-primary)' }}>{item.category}</span>
                            <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', textAlign: 'right' }}>{fmt(item.amountBase)}</span>
                            <span style={{ fontSize: '11px', fontWeight: '600', textAlign: 'right', color: over ? '#ef4444' : pct > 80 ? '#f59e0b' : '#10b981' }}>{fmt(item.actualSpentBase)}{over ? ' ↑' : ''}</span>
                        </div>
                    );
                })}
                {allBudgetItems.filter(i => i.enabled).length === 0 && (
                    <div style={{ fontSize: '13px', color: 'var(--text-tertiary)', textAlign: 'center', padding: '8px 0' }}>No budget data</div>
                )}
            </div>

            {/* Ask Advisor Chat */}
            <div style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-tertiary)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '8px' }}>Ask Advisor (Claude API)</div>
            <FIAdvisorChat report={report} generateFIReport={generateFIReport} />
        </div>
    );
}
